import * as React from 'react';
import classnames from 'classnames';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { __t, applyTranslations } from 'utils/language';
import { mapResponseTypeToCategory } from 'utils/influencers';

import type { ResponseTime } from 'types';

import './response-time-info.scss';

applyTranslations(require('./response-time-info.i18n.json'));

interface CreatorProfileAlike {
  isLightweight?: boolean
}

type Props<T> = {
  hidePopover?: boolean,
  responseTime: ResponseTime | undefined | null,
  creatorProfile: T,
  isForCreator?: boolean,
  showQuestionMark?: boolean,
  abbreviatedBrandLabel?: boolean,
  className?: string
};

function getWidth(type: string, isForCreator: boolean) {
  if (type === 'lightweight' || isForCreator) {
    return 'extra-long';
  }

  return 'normal';
}

const ResponseTimeInfo = <T extends CreatorProfileAlike>(
  {
    responseTime,
    creatorProfile,
    isForCreator = false,
    showQuestionMark = false,
    hidePopover = false,
    abbreviatedBrandLabel = false,
    className
  }: Props<T>
): React.ReactElement | null => {
  const containerClassName = classnames('response-time-info', className, {
    'response-time-info--creator': isForCreator,
    'response-time-info--has-popover': !hidePopover
  });

  const mode = (isForCreator) ? 'creator' : 'brand';

  const type = (creatorProfile.isLightweight)
    ? 'lightweight'
    : mapResponseTypeToCategory(responseTime);

  if (!type) {
    return null;
  }

  const responseTimeInfoTextNormal = __t(`response_time_info.${mode}.${type}.label`);
  let responseTimeInfoTextShort = __t(`response_time_info.${mode}.${type}.label_short`);

  if (mode === 'brand' && abbreviatedBrandLabel) {
    responseTimeInfoTextShort = __t(`response_time_info.brand.${type}.label_abbreviated`);
  }

  if (showQuestionMark) {
    return (
      <section className={containerClassName}>
        <div
          className="response-time-info__message"
          title={responseTimeInfoTextShort}
        >
          {responseTimeInfoTextShort}
        </div>
        <div className="response-time-info__mark">
          <Tooltip
            message={__t(`response_time_info.${mode}.${type}.explanation`)}
            display="block"
            width={getWidth(type, isForCreator)}
            showQuestionMark
          />
        </div>
      </section>
    );
  }

  const container = (
    <section className={containerClassName}>
      <SVGDynamicIcon
        width={12}
        height={16}
        name="hourglass-custom-bg"
        customColor="grey"
      />
      <div
        className="response-time-info__message"
        title={responseTimeInfoTextNormal}
      >
        {responseTimeInfoTextNormal}
      </div>
    </section>
  );

  return (
    hidePopover ?
      (
        <div>{container}</div>
      )
      : (
        <Tooltip
          message={__t(`response_time_info.${mode}.${type}.explanation`)}
          display="block"
          width={getWidth(type, isForCreator)}
        >
          {container}
        </Tooltip>
      )
  );
};

export default ResponseTimeInfo;
